import SubHeaderTab from "./SubHeaderTab";
import type { SubHeaderTabData } from "./SubHeaderTab";

import style from "./style.module.css";

/** SubHeaderTab's own tab shape, plus the alternate urls read here. */
interface SubHeaderTabsTab extends SubHeaderTabData {
  altUrls?: string[];
}

interface SubHeaderTabsProps {
  tabs: Record<string, SubHeaderTabsTab>;
}

/**
 * @desc - takes a tabs object and adds isSelected and lastWasSelected based on the current window location
 * @param {object} tabs - the reference objects for the tabs to process
 * @param {string} tabs.url - the url link for the tab, if necessary (some tabs may not be links)
 * @returns {[object]} - returns the tabs object converted to an array with the lastWasSelected and isSelected keys appended to each
 */
const processTabSelection = (tabs: Record<string, SubHeaderTabsTab>) => {
  const pathName = window.location.pathname;

  const processedTabs = Object.values(tabs).reduce<SubHeaderTabsTab[]>(
    (previous, current) => {
      // tabs can pass an array of alternate Urls for them to show as active for
      const matchesAltUrl = current.altUrls
        ? current.altUrls.reduce(
            (previousValue, altUrl) =>
              previousValue || pathName.includes(altUrl),
            false
          )
        : false;
      // String() reproduces the coercion includes() already performed on the
      // tabs that carry no url.
      const isSelected =
        pathName.includes(String(current.url)) || matchesAltUrl;
      current.isSelected = isSelected;
      if (!isSelected && previous.length) {
        current.lastWasSelected = previous[previous.length - 1]?.isSelected;
      }
      return previous.concat([current]);
    },
    []
  );

  return processedTabs;
};

export const SubHeaderTabs = ({ tabs }: SubHeaderTabsProps) => {
  /* note: processTabSelection converts tabs object into an array of tabs */
  const processedTabs = processTabSelection(tabs);

  return (
    <div className={style.subHeaderTabsWrapper} data-testid="SubHeaderTabs">
      {processedTabs.map(tab => (
        // React stringifies keys, so String() keeps component titles working.
        <SubHeaderTab tab={tab} key={String(tab.title)} />
      ))}
    </div>
  );
};

SubHeaderTabs.displayName = "SubHeaderTabs";

export default SubHeaderTabs;
